"use client";

import { SacredCard } from "@/components/ui/SacredPrimitives";
import { TurnstileGate } from "@/components/TurnstileGate";
import { trackPostPurchaseAgentFollowupClicked } from "@/lib/analytics";
import { AbandonedPanel, FollowupPanel, RemedyKitPanel } from "./PaidReportCTA.panels";
import { usePaidReportCTA } from "./PaidReportCTA.hook";
import type { PaidReportCTAProps } from "./PaidReportCTA.types";
import { safeSessionSet } from "@/lib/browser-storage";
import { startReportInputCollection } from "./PaidReportCTA.collect-inputs";

export type { ReportPlanId } from "./PaidReportCTA.types";

export default function PaidReportCTA({
  planId,
  priceInr,
  headline,
  subhead,
  reportInputs,
  readingId,
  sessionId,
  language,
  prefill,
  remedyUpsellProducts,
  source,
  collectInputsHref,
  collectInputsLabel,
  collectInputsNote,
  analyticsContext,
  onAgentMemoryEvent,
  autoOpen,
}: PaidReportCTAProps) {
  const uiLanguage = reportUiLanguage(language, reportInputs);
  const {
    paying,
    error,
    downloadUrl,
    followup,
    checkoutAbandoned,
    recoveryHref,
    checkoutEmail,
    checkoutPhone,
    remedyKit,
    setCheckoutEmail,
    setCheckoutPhone,
    handleBuy,
  } = usePaidReportCTA({
    planId,
    priceInr,
    reportInputs,
    readingId,
    sessionId,
    prefill,
    remedyUpsellProducts,
    source,
    analyticsContext,
    onAgentMemoryEvent,
    autoOpen,
  });

  function startAgentFollowup() {
    if (!followup) return;
    trackPostPurchaseAgentFollowupClicked({
      plan_id: followup.planId,
      order_id: followup.orderId,
      source,
      placement: analyticsContext?.placement,
      intent: analyticsContext?.intent,
      source_path: analyticsContext?.sourcePath,
      customer_problem: analyticsContext?.customerProblem,
    });
    try {
      safeSessionSet("seo_entry_prompt", followup.prompt);
    } catch {
      // Link still works when storage is unavailable.
    }
    window.location.href = `/free-kundali/?post_purchase_report=${encodeURIComponent(
      followup.planId
    )}&order=${encodeURIComponent(followup.orderId)}`;
  }

  return (
    <SacredCard
      className="p-5 sm:p-6 text-center"
      style={{
        background: "linear-gradient(135deg, var(--color-gold-light, #fff8e1) 0%, #fff 100%)",
        borderColor: "var(--color-accent-border)",
      }}
    >
      {/* Invisible anti-bot widget — readies a Turnstile token for checkout on
          surfaces without the chat panel. No-op until the sitekey is set. */}
      {collectInputsHref ? null : <TurnstileGate />}
      <p
        className="text-[11px] font-bold uppercase tracking-wider mb-2"
        style={{ color: "var(--ish-accent)", letterSpacing: "0.12em" }}
      >
        Premium Report
      </p>
      <h3
        className="text-[18px] sm:text-[20px] font-bold mb-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        {headline}
      </h3>
      <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
        {subhead}
      </p>
      <ReportCTAAction
        analyticsContext={analyticsContext}
        checkoutEmail={checkoutEmail}
        checkoutPhone={checkoutPhone}
        collectInputsHref={collectInputsHref}
        collectInputsLabel={collectInputsLabel}
        handleBuy={handleBuy}
        language={uiLanguage}
        paying={paying}
        planId={planId}
        priceInr={priceInr}
        setCheckoutEmail={setCheckoutEmail}
        setCheckoutPhone={setCheckoutPhone}
      />
      <p className="mt-3 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
        {footerCopy(uiLanguage, Boolean(collectInputsHref), collectInputsNote)}
      </p>
      {checkoutAbandoned && !followup && (
        <AbandonedPanel
          planId={planId}
          priceInr={priceInr}
          source={source}
          recoveryHref={recoveryHref}
          analyticsContext={analyticsContext}
          onRetry={handleBuy}
        />
      )}
      {followup && downloadUrl && (
        <FollowupPanel
          followup={followup}
          downloadUrl={downloadUrl}
          onAskChart={startAgentFollowup}
        />
      )}
      {remedyKit && (
        <RemedyKitPanel
          planId={planId}
          source={source}
          analyticsContext={analyticsContext}
          remedyKit={remedyKit}
        />
      )}
      {error && (
        <p role="alert" className="mt-3 text-[12px]" style={{ color: "var(--color-error-text)" }}>
          {error}
        </p>
      )}
    </SacredCard>
  );
}

function ReportCTAAction({
  analyticsContext,
  checkoutEmail,
  checkoutPhone,
  collectInputsHref,
  collectInputsLabel,
  handleBuy,
  language,
  paying,
  planId,
  priceInr,
  setCheckoutEmail,
  setCheckoutPhone,
}: Pick<
  PaidReportCTAProps,
  "analyticsContext" | "collectInputsHref" | "collectInputsLabel" | "planId" | "priceInr"
> & {
  checkoutEmail: string;
  checkoutPhone: string;
  handleBuy: () => Promise<void>;
  language: "en" | "hi";
  paying: boolean;
  setCheckoutEmail: (value: string) => void;
  setCheckoutPhone: (value: string) => void;
}) {
  if (collectInputsHref) {
    return (
      <button
        type="button"
        onClick={() =>
          startReportInputCollection({ analyticsContext, href: collectInputsHref, planId })
        }
        className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-[14px] text-white transition hover:opacity-90 disabled:opacity-50"
        style={{ background: "var(--ish-accent, #b9644a)" }}
      >
        {collectInputsLabel ?? collectInputsButtonLabel(language, priceInr)}
      </button>
    );
  }

  return (
    /* <form> wrapper so Enter on the phone/email field submits — mobile
        users tap their "Go" key instead of having to scroll to the button.
        onSubmit guards against the implicit GET that Enter would otherwise
        trigger in HTML forms. */
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!paying) handleBuy();
      }}
    >
      <CheckoutContactFields
        checkoutEmail={checkoutEmail}
        checkoutPhone={checkoutPhone}
        language={language}
        setCheckoutEmail={setCheckoutEmail}
        setCheckoutPhone={setCheckoutPhone}
      />
      <button
        type="submit"
        disabled={paying}
        className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-[14px] text-white transition hover:opacity-90 disabled:opacity-50"
        style={{ background: "var(--ish-accent, #b9644a)" }}
      >
        {paying ? loadingLabel(language) : checkoutButtonLabel(language, priceInr)}
      </button>
    </form>
  );
}

function CheckoutContactFields({
  checkoutEmail,
  checkoutPhone,
  language,
  setCheckoutEmail,
  setCheckoutPhone,
}: {
  checkoutEmail: string;
  checkoutPhone: string;
  language: "en" | "hi";
  setCheckoutEmail: (value: string) => void;
  setCheckoutPhone: (value: string) => void;
}) {
  return (
    <div className="mx-auto mb-3 max-w-sm">
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          type="email"
          name="email"
          value={checkoutEmail}
          onChange={(event) => setCheckoutEmail(event.target.value)}
          placeholder="Email"
          autoComplete="email"
          inputMode="email"
          enterKeyHint="next"
          className="min-w-0 rounded-lg border px-3 py-2 text-[13px] outline-none"
          style={checkoutInputStyle}
        />
        <input
          type="tel"
          name="phone"
          value={checkoutPhone}
          onChange={(event) => setCheckoutPhone(event.target.value)}
          placeholder={language === "hi" ? "Mobile number" : "Mobile (10 digits)"}
          autoComplete="tel"
          inputMode="numeric"
          pattern="[0-9+\-\s]*"
          maxLength={15}
          enterKeyHint="go"
          className="min-w-0 rounded-lg border px-3 py-2 text-[13px] outline-none"
          style={checkoutInputStyle}
        />
      </div>
      {/* Tell users they only need ONE — the per-form gate (PaidReportCTA.hook.ts
          missingContact) requires at-least-one, not both. Without this hint
          users assume both required and abandon when only one is handy. */}
      <p className="mt-1.5 text-center text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
        {language === "hi"
          ? "Email se receipt aur checkout help milegi; mobile report delivery ke liye."
          : "Email gets receipt and checkout help; mobile keeps report delivery reachable."}
      </p>
    </div>
  );
}

const checkoutInputStyle = {
  borderColor: "var(--ish-border)",
  color: "var(--ish-text)",
  background: "var(--ish-bg)",
};

function reportUiLanguage(
  language: PaidReportCTAProps["language"],
  reportInputs: PaidReportCTAProps["reportInputs"]
): "en" | "hi" {
  return language === "hi" || reportInputs.language === "hi" ? "hi" : "en";
}

function collectInputsButtonLabel(language: "en" | "hi", priceInr: number): string {
  return language === "hi"
    ? `Janam details bharein - ₹${priceInr}`
    : `Enter birth details - ₹${priceInr}`;
}

function checkoutButtonLabel(language: "en" | "hi", priceInr: number): string {
  return language === "hi" ? `Report paayein - ₹${priceInr}` : `Get Report - ₹${priceInr}`;
}

function loadingLabel(language: "en" | "hi"): string {
  return language === "hi" ? "Checkout khul raha hai..." : "Loading...";
}

function footerCopy(language: "en" | "hi", collectingInputs: boolean, override?: string): string {
  if (override) return override;
  if (collectingInputs) {
    return language === "hi"
      ? "Pehle birth details bharein. Chart ready hote hi checkout khulega."
      : "Enter birth details first. Checkout opens after the chart has the inputs needed for your report.";
  }
  return language === "hi"
    ? "Secure Razorpay checkout - instant PDF - Swiss Ephemeris calculation"
    : "Secure Razorpay checkout - instant PDF download - Swiss Ephemeris calculations";
}
